import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import {
  Lead, RATINGS, SERVICE_TYPES, PAYMENT_MODES, FOLLOWUP_TYPES, useLeadMasters, useLeadUsers,
} from './leads.api';

/**
 * Add / edit one lead.
 *
 * Grouped the way the conversation actually goes — who they are, where they are, what they ship,
 * where it sits in the pipeline — rather than as one 40-field wall. Only Company Name is mandatory:
 * a lead is often half a phone call, and a form that demands GSTIN before it will save is a form
 * people keep in a notebook instead.
 */

interface Props {
  lead?: Lead | null;
  onClose: () => void;
  onSaved?: (lead: Lead) => void;
}

const inputCls = 'w-full border rounded px-2 py-1.5 text-sm dark:bg-slate-900 dark:border-slate-700';
const labelCls = 'text-[11px] font-medium text-gray-500 uppercase tracking-wide';

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-gray-400 mt-0.5">{hint}</span>}
    </label>
  );
}

export function LeadFormModal({ lead, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const { data: masters } = useLeadMasters();
  const { data: users } = useLeadUsers();
  const isEdit = !!lead?.id;

  const [f, setF] = useState<any>(() => ({
    companyName: '', brandName: '', contactName: '', designation: '', mobile: '', altMobile: '',
    email: '', website: '', address: '', city: '', state: '', pincode: '', gstin: '', pan: '',
    serviceType: '', monthlyOrders: '', avgWeightKg: '', avgOrderValue: '', productCategory: '',
    currentCourier: '', paymentMode: '', codPercent: '', salesChannel: '', pickupCity: '',
    pickupPincode: '', pickupLocations: '', deliveryZones: '', expectedRate: '',
    hasWarehouse: false, wantsWms: false,
    statusId: '', sourceId: '', ownerId: '', rating: '', estimatedValue: '', expectedCloseDate: '',
    lostReason: '', notes: '', customFields: {} as Record<string, any>,
    followupAt: '', followupType: 'CALL', followupNotes: '',
  }));
  const [tagIds, setTagIds] = useState<(string | number)[]>([]);

  useEffect(() => {
    if (!lead) return;
    setF((prev: any) => ({
      ...prev,
      ...Object.fromEntries(
        Object.keys(prev).map((k) => {
          const v = (lead as any)[k];
          if (k === 'customFields') return [k, (lead.customFields ?? {}) as any];
          if (k === 'expectedCloseDate') return [k, lead.expectedCloseDate ? String(lead.expectedCloseDate).slice(0, 10) : ''];
          if (v === null || v === undefined) return [k, typeof prev[k] === 'boolean' ? false : ''];
          return [k, v];
        }),
      ),
      followupAt: '', followupType: 'CALL', followupNotes: '',
    }));
    setTagIds((lead.tags ?? []).map((t) => t.id));
  }, [lead]);

  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const setCustom = (key: string, v: any) => setF((p: any) => ({ ...p, customFields: { ...(p.customFields ?? {}), [key]: v } }));

  const activeFields = useMemo(() => (masters?.fields ?? []).filter((x) => x.isActive), [masters]);

  const save = useMutation({
    mutationFn: async () => {
      const body: any = { ...f, tagIds };
      // Empty strings must reach the API as nulls, not as the string "" — otherwise clearing a
      // number field would try to save NaN and clearing a dropdown would store a blank id.
      for (const k of Object.keys(body)) if (body[k] === '') body[k] = null;
      if (!isEdit) {
        if (!body.followupAt) { delete body.followupAt; delete body.followupType; delete body.followupNotes; }
      } else {
        delete body.followupAt; delete body.followupType; delete body.followupNotes;
      }
      const r = isEdit
        ? await api.patch(`/leads/${lead!.id}`, body)
        : await api.post('/leads', body);
      return r.data as Lead;
    },
    onSuccess: (saved) => {
      toast.success(isEdit ? 'Lead updated' : `Lead ${saved.leadNo} added`);
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead-dashboard'] });
      qc.invalidateQueries({ queryKey: ['lead-pipeline'] });
      onSaved?.(saved);
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the lead'),
  });

  const statusOptions = (masters?.statuses ?? []).filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name }));
  const sourceOptions = (masters?.sources ?? []).filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name }));
  const userOptions = (users ?? []).map((u) => ({ value: u.id, label: u.name }));

  return (
    <div className="fixed inset-0 z-[900] bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-5xl my-6">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white dark:bg-slate-900 rounded-t-lg">
          <div>
            <h2 className="text-lg font-semibold">{isEdit ? `Edit ${lead!.leadNo}` : 'Add Lead'}</h2>
            <p className="text-xs text-gray-500">Only the company name is required — fill the rest as you learn it.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* --- who ------------------------------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Company &amp; contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <Field label="Company Name *">
                  <input className={inputCls} value={f.companyName} onChange={(e) => set('companyName', e.target.value)} autoFocus />
                </Field>
              </div>
              <Field label="Brand"><input className={inputCls} value={f.brandName} onChange={(e) => set('brandName', e.target.value)} /></Field>
              <Field label="Website"><input className={inputCls} value={f.website} onChange={(e) => set('website', e.target.value)} /></Field>
              <Field label="Contact Person"><input className={inputCls} value={f.contactName} onChange={(e) => set('contactName', e.target.value)} /></Field>
              <Field label="Designation"><input className={inputCls} value={f.designation} onChange={(e) => set('designation', e.target.value)} /></Field>
              <Field label="Mobile" hint="Used by the call and WhatsApp buttons">
                <input className={inputCls} value={f.mobile} onChange={(e) => set('mobile', e.target.value)} />
              </Field>
              <Field label="Alt Mobile"><input className={inputCls} value={f.altMobile} onChange={(e) => set('altMobile', e.target.value)} /></Field>
              <div className="md:col-span-2">
                <Field label="Email"><input className={inputCls} value={f.email} onChange={(e) => set('email', e.target.value)} /></Field>
              </div>
              <Field label="GSTIN"><input className={inputCls} value={f.gstin} onChange={(e) => set('gstin', e.target.value.toUpperCase())} /></Field>
              <Field label="PAN"><input className={inputCls} value={f.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())} /></Field>
            </div>
          </section>

          {/* --- where ----------------------------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-4">
                <Field label="Address"><input className={inputCls} value={f.address} onChange={(e) => set('address', e.target.value)} /></Field>
              </div>
              <Field label="City"><input className={inputCls} value={f.city} onChange={(e) => set('city', e.target.value)} /></Field>
              <Field label="State"><input className={inputCls} value={f.state} onChange={(e) => set('state', e.target.value)} /></Field>
              <Field label="Pincode"><input className={inputCls} value={f.pincode} onChange={(e) => set('pincode', e.target.value)} /></Field>
            </div>
          </section>

          {/* --- logistics profile ----------------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              Shipping profile <span className="font-normal text-gray-400">— what a rate quote is built from</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Service Type">
                <select className={inputCls} value={f.serviceType} onChange={(e) => set('serviceType', e.target.value)}>
                  <option value="">—</option>
                  {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Monthly Orders"><input type="number" className={inputCls} value={f.monthlyOrders} onChange={(e) => set('monthlyOrders', e.target.value)} /></Field>
              <Field label="Avg Weight (kg)"><input type="number" step="0.001" className={inputCls} value={f.avgWeightKg} onChange={(e) => set('avgWeightKg', e.target.value)} /></Field>
              <Field label="Avg Order Value"><input type="number" className={inputCls} value={f.avgOrderValue} onChange={(e) => set('avgOrderValue', e.target.value)} /></Field>
              <Field label="Product Category"><input className={inputCls} value={f.productCategory} onChange={(e) => set('productCategory', e.target.value)} /></Field>
              <Field label="Current Courier" hint="Who they ship with today"><input className={inputCls} value={f.currentCourier} onChange={(e) => set('currentCourier', e.target.value)} /></Field>
              <Field label="Payment Mode">
                <select className={inputCls} value={f.paymentMode} onChange={(e) => set('paymentMode', e.target.value)}>
                  <option value="">—</option>
                  {PAYMENT_MODES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="COD %"><input type="number" className={inputCls} value={f.codPercent} onChange={(e) => set('codPercent', e.target.value)} /></Field>
              <Field label="Sales Channel" hint="Shopify / Amazon / Website / Offline"><input className={inputCls} value={f.salesChannel} onChange={(e) => set('salesChannel', e.target.value)} /></Field>
              <Field label="Pickup City"><input className={inputCls} value={f.pickupCity} onChange={(e) => set('pickupCity', e.target.value)} /></Field>
              <Field label="Pickup Pincode"><input className={inputCls} value={f.pickupPincode} onChange={(e) => set('pickupPincode', e.target.value)} /></Field>
              <Field label="Pickup Locations"><input type="number" className={inputCls} value={f.pickupLocations} onChange={(e) => set('pickupLocations', e.target.value)} /></Field>
              <Field label="Delivery Zones" hint="North / Pan-India / …"><input className={inputCls} value={f.deliveryZones} onChange={(e) => set('deliveryZones', e.target.value)} /></Field>
              <Field label="Expected Rate" hint="The rate they are asking for"><input type="number" className={inputCls} value={f.expectedRate} onChange={(e) => set('expectedRate', e.target.value)} /></Field>
              <label className="flex items-center gap-2 text-sm mt-5">
                <input type="checkbox" checked={!!f.hasWarehouse} onChange={(e) => set('hasWarehouse', e.target.checked)} /> Has warehouse
              </label>
              <label className="flex items-center gap-2 text-sm mt-5">
                <input type="checkbox" checked={!!f.wantsWms} onChange={(e) => set('wantsWms', e.target.checked)} /> Interested in WMS
              </label>
            </div>
          </section>

          {/* --- pipeline -------------------------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Pipeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Status">
                <SearchableSelect value={f.statusId} onChange={(v) => set('statusId', v)} options={statusOptions} placeholder="Default status" allowClear />
              </Field>
              <Field label="Source">
                <SearchableSelect value={f.sourceId} onChange={(v) => set('sourceId', v)} options={sourceOptions} placeholder="Where it came from" allowClear />
              </Field>
              <Field label="Sale Person">
                <SearchableSelect value={f.ownerId} onChange={(v) => set('ownerId', v)} options={userOptions} placeholder="Unassigned" allowClear />
              </Field>
              <Field label="Rating">
                <select className={inputCls} value={f.rating} onChange={(e) => set('rating', e.target.value)}>
                  <option value="">—</option>
                  {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Estimated Value" hint="Expected monthly business"><input type="number" className={inputCls} value={f.estimatedValue} onChange={(e) => set('estimatedValue', e.target.value)} /></Field>
              <Field label="Expected Close"><input type="date" className={inputCls} value={f.expectedCloseDate} onChange={(e) => set('expectedCloseDate', e.target.value)} /></Field>
              <div className="md:col-span-2">
                <Field label="Tags">
                  <MultiSelectFilter
                    label="" placeholder="Add tags" values={tagIds} onChange={setTagIds}
                    options={(masters?.tags ?? []).filter((t) => t.isActive).map((t) => ({ value: t.id, label: t.name }))}
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Lost Reason" hint="Only when the status is a Lost one"><input className={inputCls} value={f.lostReason} onChange={(e) => set('lostReason', e.target.value)} /></Field>
              </div>
              <div className="md:col-span-4">
                <Field label="Notes"><textarea rows={2} className={inputCls} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></Field>
              </div>
            </div>
          </section>

          {/* --- custom columns from Settings ------------------------------- */}
          {activeFields.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                Custom columns <span className="font-normal text-gray-400">— from Leads → Settings</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {activeFields.map((cf) => {
                  const val = (f.customFields ?? {})[cf.key] ?? '';
                  return (
                    <Field key={cf.id} label={cf.label + (cf.isRequired ? ' *' : '')}>
                      {cf.fieldType === 'SELECT' ? (
                        <select className={inputCls} value={val} onChange={(e) => setCustom(cf.key, e.target.value)}>
                          <option value="">—</option>
                          {(cf.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : cf.fieldType === 'CHECKBOX' ? (
                        <input type="checkbox" className="mt-2" checked={!!val} onChange={(e) => setCustom(cf.key, e.target.checked)} />
                      ) : (
                        <input
                          type={cf.fieldType === 'NUMBER' ? 'number' : cf.fieldType === 'DATE' ? 'date' : 'text'}
                          className={inputCls} value={val} onChange={(e) => setCustom(cf.key, e.target.value)}
                        />
                      )}
                    </Field>
                  );
                })}
              </div>
            </section>
          )}

          {/* --- first follow-up, only when adding --------------------------- */}
          {!isEdit && (
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                Plan the first follow-up <span className="font-normal text-gray-400">— optional, but this is what puts it on someone&apos;s list</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Field label="When"><input type="datetime-local" className={inputCls} value={f.followupAt} onChange={(e) => set('followupAt', e.target.value)} /></Field>
                <Field label="How">
                  <select className={inputCls} value={f.followupType} onChange={(e) => set('followupType', e.target.value)}>
                    {FOLLOWUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Notes"><input className={inputCls} value={f.followupNotes} onChange={(e) => set('followupNotes', e.target.value)} /></Field>
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white dark:bg-slate-900 rounded-b-lg">
          <button onClick={onClose} className="px-3 py-2 border rounded text-sm">Cancel</button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !String(f.companyName ?? '').trim()}
            className="px-4 py-2 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {save.isPending && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Save changes' : 'Add lead'}
          </button>
        </div>
      </div>
    </div>
  );
}
